<template>
  <aside class="gmail-sidebar">
    <div class="gmail-sidebar-header">
      <span>Gmail</span>
      <button
        type="button"
        class="gmail-close-btn"
        aria-label="Gmail を閉じる"
        @click="dashboardStore.closeGmailSidebar()"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="gmail-sidebar-content">
      <template v-if="dashboardStore.isLoadingEmails">
        <div class="gmail-loading">
          <i class="fas fa-spinner fa-spin"></i>
          <p>メール取得中...</p>
        </div>
      </template>
      <template v-else-if="errorMessage">
        <div class="gmail-error">
          <i class="fas fa-exclamation-circle"></i>
          <h2>エラーが発生しました</h2>
          <p>{{ errorMessage }}</p>
          <button type="button" class="btn-retry" @click="loadMailInbox">
            再度読み込む
          </button>
          <p class="gmail-help">
            💡 メールサーバーが起動していることを確認してください。<br />
            ターミナルで <code>npm run server</code> を実行してください。
          </p>
        </div>
      </template>
      <template v-else-if="dashboardStore.emails.length > 0">
        <div class="gmail-email-list">
          <div
            v-for="email in dashboardStore.emails"
            :key="email.id"
            class="gmail-email-item"
          >
            <div class="gmail-email-from">{{ email.from }}</div>
            <div class="gmail-email-subject">{{ email.subject }}</div>
            <div class="gmail-email-snippet">
              {{ email.snippet.substring(0, 80) }}
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="gmail-empty">
          <p>メールが見つかりません</p>
        </div>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import type { Email } from "@/types/models";

const dashboardStore = useDashboardStore();
const errorMessage = ref<string | null>(null);

const MAIL_CONFIG = {
  API_URL: "http://localhost:3001/api/mail",
};

onMounted(() => {
  loadMailInbox();
});

async function loadMailInbox(): Promise<void> {
  dashboardStore.setLoadingEmails(true);
  errorMessage.value = null;

  try {
    const response = await fetch(`${MAIL_CONFIG.API_URL}/inbox?limit=10`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "メール取得に失敗しました");
    }

    dashboardStore.setEmails(data.emails || []);
  } catch (error) {
    console.error("Mail fetch error:", error);
    errorMessage.value =
      error instanceof Error ? error.message : "メール取得に失敗しました";
  } finally {
    dashboardStore.setLoadingEmails(false);
  }
}
</script>

<style scoped>
.gmail-sidebar {
  position: absolute;
  top: 64px;
  right: 0;
  width: 400px;
  height: calc(100vh - 64px);
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 200;
}

.gmail-sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  font-weight: 600;
}

.gmail-close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #5f6368;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}

.gmail-close-btn:hover {
  color: #202124;
}

.gmail-sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.gmail-loading,
.gmail-error,
.gmail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #5f6368;
}

.gmail-loading i {
  font-size: 24px;
  color: #1f73e6;
  margin-bottom: 12px;
}

.gmail-error i {
  font-size: 48px;
  color: #d33b27;
  margin-bottom: 16px;
}

.gmail-error h2 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #202124;
}

.gmail-error p {
  margin: 8px 0;
  font-size: 12px;
}

.gmail-error .btn-retry {
  margin-top: 12px;
  padding: 8px 12px;
  background: #1f73e6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.2s;
}

.gmail-error .btn-retry:hover {
  background: #1557b0;
}

.gmail-help {
  font-size: 11px;
  margin-top: 16px;
  color: #9aa0a6;
}

.gmail-help code {
  background: #f5f5f5;
  padding: 2px 4px;
  border-radius: 2px;
  font-family: monospace;
}

.gmail-email-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.gmail-email-item {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 12px;
}

.gmail-email-from {
  font-weight: 600;
  color: #202124;
  margin-bottom: 4px;
}

.gmail-email-subject {
  color: #1f73e6;
  margin-bottom: 4px;
  word-break: break-word;
}

.gmail-email-snippet {
  color: #5f6368;
  word-break: break-word;
}
</style>
